///////////////////////////////////////////////////////////////////////////////
// Voice search — the mic button + language chip for a search box.
//
// Speech capture runs on the device through the Web Speech API (Chrome,
// Edge, Safari 14.1+, Samsung Internet); nothing is recorded or uploaded.
// The browser hands back text, the caller sends it through
// POST /api/search/parse exactly like typed text (source 'voice'). Browsers
// without SpeechRecognition (Firefox, some webviews) simply never see the
// button. Plan and decisions: docs/todo/voice-search.md
//
//   <VoiceButton
//     onInterim={(text) => ...}      // live transcript while speaking
//     onTranscript={(text) => ...}   // final transcript — go search
//     onState={(state) => ...}       // 'idle' | 'listening' | 'denied' | 'nospeech' | 'error'
//     disabled={bool}
//   />
///////////////////////////////////////////////////////////////////////////////
import React, { useEffect, useRef, useState } from 'react';
import { readLang, track } from './Chrome';
import { VOICE_LOCALES, VOICE_LANG_KEY, pickVoiceLocale } from './voiceLocale';

function Recognition() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export function speechSupported() {
  return !!Recognition();
}

function readStoredLocale() {
  try { return window.localStorage.getItem(VOICE_LANG_KEY) || null; } catch { return null; }
}
function writeStoredLocale(code) {
  try { window.localStorage.setItem(VOICE_LANG_KEY, code); } catch { /* private mode */ }
}

export function initialVoiceLocale() {
  return pickVoiceLocale({
    stored: readStoredLocale(),
    siteLang: readLang(),
    browserLang: typeof navigator !== 'undefined' ? (navigator.languages && navigator.languages[0]) || navigator.language : null,
  });
}

export function VoiceButton({ onInterim, onTranscript, onState, disabled }) {
  const [locale, setLocale] = useState(initialVoiceLocale);
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const finalRef = useRef('');
  const failedRef = useRef(false); // onerror fired for this session

  useEffect(() => () => { if (recRef.current) { try { recRef.current.abort(); } catch { /* */ } } }, []);

  if (!speechSupported()) return null;

  function state(s) { if (onState) onState(s); }

  function stop() {
    if (recRef.current) { try { recRef.current.stop(); } catch { /* */ } }
  }

  function start() {
    if (listening) return stop();
    const Rec = Recognition();
    const rec = new Rec();
    rec.lang = locale;
    rec.interimResults = true;
    rec.maxAlternatives = 1;
    rec.continuous = false;
    finalRef.current = '';
    failedRef.current = false;
    rec.onstart = () => { setListening(true); state('listening'); track('voice_search', { stage: 'start', lang: locale }); };
    rec.onresult = (e) => {
      let interim = '';
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const r = e.results[i];
        if (r.isFinal) final += r[0].transcript; else interim += r[0].transcript;
      }
      if (final) finalRef.current += final;
      const shown = (finalRef.current + ' ' + interim).replace(/\s+/g, ' ').trim();
      if (onInterim) onInterim(shown);
    };
    rec.onerror = (e) => {
      const code = e && e.error;
      failedRef.current = true;
      track('voice_search', { stage: 'error', lang: locale, error: code });
      state(code === 'not-allowed' || code === 'service-not-allowed' ? 'denied' : (code === 'no-speech' ? 'nospeech' : 'error'));
    };
    rec.onend = () => {
      setListening(false);
      recRef.current = null;
      const text = finalRef.current.replace(/\s+/g, ' ').trim();
      if (text) {
        track('voice_search', { stage: 'result', lang: locale, chars: text.length });
        state('idle');
        if (onTranscript) onTranscript(text);
      } else if (!failedRef.current) {
        state('nospeech'); // stopped without a word; onerror already explained any failure
      }
    };
    recRef.current = rec;
    try { rec.start(); } catch (err) { setListening(false); state('error'); }
  }

  function changeLocale(e) {
    const code = e.target.value;
    setLocale(code);
    writeStoredLocale(code);
    track('voice_search', { stage: 'lang', lang: code });
  }

  return (
    <span className="voice">
      <select className="voice-lang" value={locale} onChange={changeLocale} aria-label="Voice search language" disabled={listening} title="Language to listen for">
        {VOICE_LOCALES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
      </select>
      <button
        type="button"
        className={`mic${listening ? ' listening' : ''}`}
        onClick={start}
        disabled={disabled}
        aria-pressed={listening}
        aria-label={listening ? 'Stop listening' : 'Search by voice'}
        title={listening ? 'Stop' : 'Search by voice'}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <rect x="5.5" y="1.5" width="5" height="8" rx="2.5" stroke="currentColor" strokeWidth="1.5" />
          <path d="M3.5 7.5a4.5 4.5 0 0 0 9 0M8 12v2.5M5.5 14.5h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </span>
  );
}

export const VOICE_HINTS = {
  listening: 'Listening… say what you need — “AIS 100 baht to 0812345678”, “Japan eSIM 5 days”, “$20 Apple gift card”',
  denied: 'Microphone access is blocked. Allow it in your browser settings, or type your search.',
  nospeech: 'Didn’t hear anything. Tap the mic and try again.',
  error: 'Voice search isn’t available right now. Type your search instead.',
};
